import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { format, parseISO, addDays, startOfWeek } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  CalendarDays, 
  Clock, 
  CheckCircle, 
  MessageSquare,
  FileText,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ArrowLeft,
  CreditCard,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppointmentsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isAuthenticated, getAuthHeader, user, token, loading: authLoading } = useAuth();
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [appointmentType, setAppointmentType] = useState('first_consultation');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [myAppointments, setMyAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [hasCompletedTests, setHasCompletedTests] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const locale = language === 'it' ? it : enUS;

  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    checkAccessAndFetch();
  }, [isAuthenticated, authLoading, navigate, token]);

  const checkAccessAndFetch = async () => {
    try {
      // Check if user has completed tests
      const response = await axios.get(`${API}/client/my-tests`, {
        headers: getAuthHeader()
      });
      
      const assignedTests = response.data.assigned_tests || [];
      const completedTests = response.data.completed_tests || [];
      
      // Access allowed if: all assigned tests are completed OR no tests assigned (paid for consultation)
      const allTestsCompleted = assignedTests.length > 0 && 
        assignedTests.every(t => completedTests.includes(t.id || t));
      
      setHasCompletedTests(allTestsCompleted);
      
      // For now, deny access if tests are assigned but not completed
      if (assignedTests.length > 0 && !allTestsCompleted) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      
      // If access is granted, fetch appointment data
      fetchData();
    } catch (error) {
      console.error('Error checking access:', error);
      // If no assigned tests, allow access (user might have paid for consultation)
      fetchData();
    }
  };

  const fetchData = async () => {
    try {
      const [slotsRes, appointmentsRes] = await Promise.all([
        axios.get(`${API}/appointments/available-slots`),
        axios.get(`${API}/appointments/my`, { headers: getAuthHeader() })
      ]);
      setAvailableSlots(slotsRes.data);
      setMyAppointments(appointmentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeSlotsForDate = (date) => {
    if (!date) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return availableSlots.filter(slot => slot.date === dateStr);
  };

  const handleBookAppointment = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error(language === 'it' ? 'Seleziona data e ora' : 'Select date and time');
      return;
    }

    // Show payment modal instead of booking directly
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = () => {
    // For now, show message that payment is coming soon
    toast.info(
      language === 'it' 
        ? 'Integrazione Satispay in arrivo! Per prenotare, contattaci: infoilmioterapeuta@gmail.com' 
        : 'Satispay integration coming soon! To book, contact us: infoilmioterapeuta@gmail.com'
    );
    setShowPaymentModal(false);
  };

  const handleCancelAppointment = async (appointmentId) => {
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`, { headers: getAuthHeader() });
      toast.success(language === 'it' ? 'Appuntamento cancellato' : 'Appointment cancelled');
      fetchData();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error(language === 'it' ? 'Errore nella cancellazione' : 'Cancellation error');
    }
  };

  const appointmentTypes = [
    { 
      value: 'first_consultation', 
      label: language === 'it' ? 'Primo Colloquio' : 'First Consultation',
      icon: MessageSquare,
      description: language === 'it' ? 'Colloquio conoscitivo con lo specialista' : 'Initial meeting with the specialist'
    },
    { 
      value: 'results_review', 
      label: language === 'it' ? 'Restituzione Risultati' : 'Results Review',
      icon: FileText,
      description: language === 'it' ? 'Discussione dei risultati dei test' : 'Discussion of test results'
    }
  ];

  // Get dates that have available slots
  const availableDates = [...new Set(availableSlots.map(slot => slot.date))];

  if (loading || authLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  // Access Denied - User needs to complete tests first
  if (accessDenied) {
    return (
      <div className="min-h-screen pt-24 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="border-0 shadow-xl rounded-3xl overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-8 text-white text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CalendarDays className="w-10 h-10" />
              </div>
              <h1 className="text-3xl font-bold">
                {language === 'it' ? 'Accesso Limitato' : 'Limited Access'}
              </h1>
            </div>
            
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <p className="text-lg text-muted-foreground">
                  {language === 'it' 
                    ? 'Per prenotare un appuntamento devi prima:' 
                    : 'To book an appointment you must first:'}
                </p>
              </div>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-800">
                      {language === 'it' ? 'Completare i Test Assegnati' : 'Complete Assigned Tests'}
                    </h3>
                    <p className="text-sm text-blue-600">
                      {language === 'it' 
                        ? 'Completa tutti i test psicologici assegnati dallo specialista per procedere.' 
                        : 'Complete all psychological tests assigned by the specialist to proceed.'}
                    </p>
                  </div>
                </div>
                
                <div className="text-center text-muted-foreground">
                  {language === 'it' ? '— oppure —' : '— or —'}
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-green-50 rounded-xl">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-800">
                      {language === 'it' ? 'Acquistare un Colloquio' : 'Purchase a Consultation'}
                    </h3>
                    <p className="text-sm text-green-600">
                      {language === 'it' 
                        ? 'Paga per un colloquio diretto con lo specialista senza test preliminari.' 
                        : 'Pay for a direct consultation with the specialist without preliminary tests.'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => navigate('/app/assessment')}
                  className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {language === 'it' ? 'Vai ai Test' : 'Go to Tests'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1 h-12 rounded-xl"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {language === 'it' ? 'Torna Indietro' : 'Go Back'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const timeSlotsForSelectedDate = getTimeSlotsForDate(selectedDate);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="appointments-page">
      <div className="container-wide">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {language === 'it' ? 'Indietro' : 'Back'}
        </Button>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-full bg-accent/20 flex items-center justify-center">
              <CalendarDays className="w-7 h-7 text-accent" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-primary mb-2">
            {language === 'it' ? 'Prenota un Appuntamento' : 'Book an Appointment'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'it' 
              ? 'Scegli la data e l\'ora per il tuo colloquio con lo specialista'
              : 'Choose the date and time for your consultation with the specialist'}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Booking Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-white border-0 shadow-medium rounded-3xl overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-xl font-serif text-primary">
                    {language === 'it' ? 'Seleziona Tipo di Appuntamento' : 'Select Appointment Type'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Appointment Type Selection */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {appointmentTypes.map((type) => {
                      const IconComponent = type.icon;
                      return (
                        <button
                          key={type.value}
                          onClick={() => setAppointmentType(type.value)}
                          className={`p-4 rounded-2xl border-2 text-left transition-all ${
                            appointmentType === type.value
                              ? 'border-accent bg-accent/5'
                              : 'border-transparent bg-secondary/30 hover:bg-secondary/50'
                          }`}
                          data-testid={`appointment-type-${type.value}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              appointmentType === type.value ? 'bg-accent/20' : 'bg-secondary'
                            }`}>
                              <IconComponent className={`w-5 h-5 ${
                                appointmentType === type.value ? 'text-accent' : 'text-muted-foreground'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium text-primary">{type.label}</p>
                              <p className="text-sm text-muted-foreground">{type.description}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Calendar */}
                  <div>
                    <h3 className="font-medium text-primary mb-4">
                      {language === 'it' ? 'Seleziona Data' : 'Select Date'}
                    </h3>
                    <div className="flex justify-center">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => {
                          setSelectedDate(date);
                          setSelectedTime(null);
                        }}
                        locale={locale}
                        disabled={(date) => {
                          const dateStr = format(date, 'yyyy-MM-dd');
                          return !availableDates.includes(dateStr);
                        }}
                        className="rounded-2xl border-0"
                        data-testid="appointment-calendar"
                      />
                    </div>
                  </div>

                  {/* Time Slots */}
                  {selectedDate && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                    >
                      <h3 className="font-medium text-primary mb-4">
                        {language === 'it' ? 'Seleziona Ora' : 'Select Time'} - {format(selectedDate, 'EEEE d MMMM', { locale })}
                      </h3>
                      {timeSlotsForSelectedDate.length > 0 ? (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                          {timeSlotsForSelectedDate.map((slot) => (
                            <button
                              key={slot.time}
                              onClick={() => setSelectedTime(slot.time)}
                              className={`py-3 px-4 rounded-xl font-medium transition-all ${
                                selectedTime === slot.time
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-secondary/50 text-primary hover:bg-secondary'
                              }`}
                              data-testid={`time-slot-${slot.time}`}
                            >
                              {slot.time}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-center py-4">
                          {language === 'it' ? 'Nessuno slot disponibile per questa data' : 'No slots available for this date'}
                        </p>
                      )}
                    </motion.div>
                  )}

                  {/* Book Button */}
                  {selectedDate && selectedTime && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="pt-4"
                    >
                      <div className="bg-accent/10 rounded-2xl p-4 mb-4">
                        <p className="text-sm text-muted-foreground mb-1">
                          {language === 'it' ? 'Riepilogo prenotazione' : 'Booking summary'}
                        </p>
                        <p className="font-medium text-primary">
                          {appointmentTypes.find(t => t.value === appointmentType)?.label}
                        </p>
                        <p className="text-primary">
                          {format(selectedDate, 'EEEE d MMMM yyyy', { locale })} - {selectedTime}
                        </p>
                      </div>
                      <Button
                        size="lg"
                        className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
                        onClick={handleBookAppointment}
                        disabled={booking}
                        data-testid="confirm-booking-btn"
                      >
                        {booking ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5 mr-2" />
                            {language === 'it' ? 'Conferma Prenotazione' : 'Confirm Booking'}
                          </>
                        )}
                      </Button>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* My Appointments */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-white border-0 shadow-medium rounded-3xl overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-xl font-serif text-primary">
                    {language === 'it' ? 'I Miei Appuntamenti' : 'My Appointments'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {myAppointments.filter(apt => apt.status !== 'cancelled').length === 0 ? (
                    <div className="text-center py-8">
                      <CalendarDays className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">
                        {language === 'it' ? 'Nessun appuntamento programmato' : 'No appointments scheduled'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myAppointments
                        .filter(apt => apt.status !== 'cancelled')
                        .map((appointment) => (
                          <div
                            key={appointment.id}
                            className="p-4 bg-secondary/30 rounded-2xl"
                            data-testid={`appointment-${appointment.id}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <Badge variant="outline" className="bg-primary/5">
                                {appointment.appointment_type === 'first_consultation'
                                  ? (language === 'it' ? 'Primo Colloquio' : 'First Consultation')
                                  : (language === 'it' ? 'Restituzione' : 'Review')}
                              </Badge>
                              <Badge className="bg-green-100 text-green-700">
                                {language === 'it' ? 'Confermato' : 'Confirmed'}
                              </Badge>
                            </div>
                            <p className="font-medium text-primary">
                              {format(parseISO(appointment.date), 'EEEE d MMMM', { locale })}
                            </p>
                            <p className="text-muted-foreground flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {appointment.time}
                            </p>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="mt-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleCancelAppointment(appointment.id)}
                              data-testid={`cancel-appointment-${appointment.id}`}
                            >
                              {language === 'it' ? 'Annulla' : 'Cancel'}
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
          >
            <div className="bg-gradient-to-r from-primary to-primary/80 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold">
                {language === 'it' ? 'Pagamento Richiesto' : 'Payment Required'}
              </h2>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-4xl font-bold text-primary mb-2">€90</p>
                <p className="text-muted-foreground">
                  {language === 'it' ? 'Colloquio con lo Specialista' : 'Specialist Consultation'}
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <h4 className="font-semibold text-slate-800 mb-2">
                  {language === 'it' ? 'Riepilogo:' : 'Summary:'}
                </h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  {selectedDate && <li>• {language === 'it' ? 'Data:' : 'Date:'} {format(selectedDate, 'EEEE d MMMM yyyy', { locale: language === 'it' ? it : enUS })}</li>}
                  {selectedTime && <li>• {language === 'it' ? 'Ora:' : 'Time:'} {selectedTime}</li>}
                </ul>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-800 text-center">
                  <strong>{language === 'it' ? 'Integrazione Satispay in arrivo!' : 'Satispay integration coming soon!'}</strong>
                  <br />
                  {language === 'it' 
                    ? 'Per effettuare il pagamento e confermare, contattaci:' 
                    : 'To make payment and confirm, contact us:'}
                  <br />
                  <a href="mailto:infoilmioterapeuta@gmail.com" className="font-semibold underline">
                    infoilmioterapeuta@gmail.com
                  </a>
                </p>
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={handlePaymentConfirm}
                  className="w-full h-12 rounded-xl"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {language === 'it' ? 'Paga con Satispay' : 'Pay with Satispay'}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setShowPaymentModal(false)}
                  className="w-full h-12 rounded-xl"
                >
                  {language === 'it' ? 'Annulla' : 'Cancel'}
                </Button>
              </div>
              
              <p className="text-xs text-center text-muted-foreground mt-4">
                <Shield className="w-3 h-3 inline mr-1" />
                {language === 'it' 
                  ? 'Pagamento sicuro e protetto' 
                  : 'Secure and protected payment'}
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsPage;
